import FasesSection from '../../sections/FasesSection';
import Descargas from '../../components/Descargas';
import { DocumentRepository2 } from '../../components/DocumentRepository';
import { motion, AnimatePresence } from "framer-motion";
import { FaFilePdf, FaProjectDiagram, FaLightbulb, FaClipboardList, FaMap } from "react-icons/fa";

import './Chapala.css';
import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Users,
  Map,
  Landmark,
  Calendar,
  Waves,
  MapPin,
  Info,
  MountainSnow,
  FileSignature,
  FileSpreadsheet,
  BarChart3,
  Zap,
  FileText,
  Compass,
  GlobeIcon,
  DownloadCloud,
  ExternalLink,
  ArrowDown,
  ClockIcon,
  Book,
  BookOpen,
  FileSearch,
  Layers,
  Target,
  LayersIcon,
  CheckCircle,
} from 'lucide-react';

const DetailBadge = ({ icon: Icon, label, value }) => (
  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl transition-all hover:bg-teal-100 dark:hover:bg-teal-900/30">
    <Icon className="text-teal-600 dark:text-teal-400 mr-3 flex-shrink-0" size={20} />
    <div className="text-sm">
      <span className="font-semibold text-gray-700 dark:text-gray-300 block leading-tight">{label}:</span>
      <span className="text-gray-900 dark:text-white font-bold">{value}</span>
    </div>
  </div>
);

const ModernBitacoraSection = ({ items }) => {
  const [expandedSection, setExpandedSection] = useState(0);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Bitácora del Programa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Registro documental completo del proceso de ordenamiento ecológico
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={index}
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-6 rounded-2xl transition-all duration-300 text-left ${
                  expandedSection === index
                    ? `bg-gradient-to-br ${item.color} text-white shadow-2xl`
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl ${
                    expandedSection === index
                      ? 'bg-white/20'
                      : `bg-gradient-to-br ${item.color}`
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                </div>
                <p className={`text-sm ${
                  expandedSection === index ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {expandedSection === index ? 'Ver documentos ↓' : 'Ver documentos →'}
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* Contenido expandido */}
        <AnimatePresence>
          {expandedSection !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                {items[expandedSection].content.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mr-3" />
                      {section.subtitle}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.items.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 
                            dark:hover:from-blue-900/20 dark:hover:to-teal-900/20 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-500 group-hover:text-teal-500 transition-colors" />
                            <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                              {doc.text}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    {items[expandedSection].content.reduce((total, section) => total + section.items.length, 0)} documentos disponibles
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const StatusBadge = () => {
  return (
    <motion.div 
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 
        backdrop-blur-md border border-emerald-500/30"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2" />
      <span className="text-sm font-medium text-white tracking-wider">FASE </span>
      <div className="ml-2 pl-2 border-l border-white/20">
        <span className="text-xs text-white/70">En Ejecución</span>
      </div>
    </motion.div>
  );
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const ModernBanner = ({ onScrollClick }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const stats = [
    { value: "7", label: "Municipios", icon: MapPin },
    { value: "2,521", label: "km²", icon: Map },
    { value: "2019", label: "Inicio", icon: Calendar },
    { value: "Vigente", label: "Estado", icon: CheckCircle },
  ];

  return (
      <section className="relative h-screen min-h-[780px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="images/Foto-Chapala.jpeg"
            alt="Paisaje Lago de Chapala"
            className="w-full h-full object-cover"
            style={{
              transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-slate-950/10" />
        </div>

        <div className="relative z-10 h-full flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="mb-6">
                  <StatusBadge />
                </div>
                <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Región Chapala</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8 max-w-xl">
                  Ordenamiento territorial y desarrollo sustentable en los municipios de Chapala, Jamay, Jocotepec, Ocotlán, Poncitlán, Tizapán el Alto y Tuxcueca.
                </p>
                <div className="flex flex-wrap gap-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    onClick={() => onScrollClick('introduccion')}
                    className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl transition-all"
                  >
                    Explorar Región
                  </motion.button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden lg:grid grid-cols-2 gap-4"
              >
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div key={index} className="text-center">
                      <Icon className="mx-auto mb-2 text-cyan-200" size={24} />
                      <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                      <div className="text-xs text-white/70 tracking-wide">{stat.label}</div>
                    </div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        >
          <ArrowDown className="text-cyan-400" size={32} />
        </motion.div>
      </section>
  );
};

const FullWidthVisorWithSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('uga');

  return (
    <section id="visor-completo" className="py-16">
      <div className="max-w-[2000px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Plataforma Geoespacial Completa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Sistema de información geográfica integrado con documentación técnica completa
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Ancho fijo */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`lg:w-96 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 sticky top-6">
              {/* Botón toggle para móviles */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mb-4 w-full p-2 bg-gradient-to-r from-blue-500 to-teal-500 
                  text-white rounded-lg flex items-center justify-center gap-2"
              >
                {sidebarOpen ? 'Ocultar Panel' : 'Mostrar Panel'}
                <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-90' : ''}`} />
              </button>

              {/* Tabs de navegación */}
              <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('uga')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'uga'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Clasificación UGA
                </button>
                <button
                  onClick={() => setActiveTab('descripcion')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'descripcion'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Descripción UGA
                </button>
                <button
                  onClick={() => setActiveTab('politica')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'politica'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Política Ambiental
                </button>
              </div>

              {/* Contenido según tab activo */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {activeTab === 'uga' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <LayersIcon className="w-5 h-5 text-blue-500 mr-2" />
                          Clasificación de UGAs
                        </h3>
                        <img
                          src="/images/Clave_UGA_Chapala.PNG"
                          alt="Clasificación de Unidades de Gestión Ambiental"
                          className="w-full h-auto rounded-lg shadow-md mb-4"
                        />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Mapa de clasificación de las Unidades de Gestión Ambiental (UGAs) 
                          de la región Chapala.
                        </p>
                      </div>
                    </>
                  )}

                  {activeTab === 'descripcion' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Info className="w-5 h-5 text-teal-500 mr-2" />
                          Descripción de las UGAs
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Las <strong>Unidades de Gestión Ambiental (UGAs)</strong> son la unidad mínima 
                          del territorio a la que se asignan determinados lineamientos y estrategias 
                          ecológicas para su manejo específico.
                        </p>
                        
                        <div className="space-y-4">
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 
                            rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Características</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                              <li>• Unidad territorial homogénea</li>
                              <li>• Criterios específicos de manejo</li>
                              <li>• Estrategias ecológicas definidas</li>
                              <li>• Monitoreo individualizado</li>
                            </ul>
                          </div>
                          
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 
                            rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Objetivos</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                              <li>• Gestión ambiental específica</li>
                              <li>• Aplicación de lineamientos</li>
                              <li>• Evaluación de resultados</li>
                              <li>• Adaptación de estrategias</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'politica' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Target className="w-5 h-5 text-purple-500 mr-2" />
                          Política Ambiental
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4 text-justify">
                          El presente <strong>Modelo de Ordenamiento Ecológico y Territorial Regional</strong> considera una zonificación, integrada por políticas que buscan definir el estado deseable del territorio mediante lineamientos, estrategias y criterios para conservar y controlar el deterioro del ambiente así como el aprovechamiento sustentable de los recursos naturales.
                        </p>
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Botones de acción */}
              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                <div className="grid grid-cols-1 gap-3">
                  <a
                    href="https://siga.jalisco.gob.mx/ordenamiento/visores/visorchapala/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-teal-500 text-white 
                      rounded-lg text-sm font-medium text-center hover:shadow-lg transition-all"
                  >
                    <ExternalLink className="w-4 h-4 inline mr-1" />
                    Abrir en Nueva Ventana
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visor - Ocupa el resto del espacio */}
          <div className="flex-1">
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <GlobeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Visor GIS Chapala</h3>
                    <p className="text-sm text-gray-400">Sistema interactivo de información geográfica</p>
                  </div>
                </div>
              </div>
              
              <div className="h-[600px]">
                <iframe
                  src="https://siga.jalisco.gob.mx/ordenamiento/visores/visorchapala/index.html"
                  className="w-full h-full border-0"
                  title="Visor GIS Chapala"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


const Items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "images/vectoriales.png", // sky-600
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/vectorialeschapala.zip",

    target: "_blank",
  },
  {
    title: "Fichas UGAss",
    description: "Informacion detallada de cada Unidad de Gestión Ambiental (UGA).",
    imageUrl: "images/FICHAS_UGA.png", // green-600
    baseColor: "green",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/Fichas%20de%20las%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Chapala.pdf",

    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio.",
    imageUrl: "images/Criterios.png", // orange-600
    baseColor: "orange",
    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Descargas/Criterios%20Regi%C3%B3n%20Chapala.pdf",
    target: "_blank",
  },
  {
    title: "Estrategias Ecologicas",
    description: "Proyectos de orientación para la protección del entorno natural.",
    imageUrl: "images/Estrategias.png", // red-600
    baseColor: "red",
    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Descargas/Estrategias%20Regi%C3%B3n%20Chapala.pdf",

    target: "_blank",
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los recursos naturales de la región.",
    imageUrl: "images/Lineamientos.png", // indigo-600
    baseColor: "indigo",    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Chapala.pdf",
    target: "_blank",
  },
  // {
  //   title: "Compatibilidades",
  //   description: "Guia de compatibilidad de  usos del suelo estratégicos.",
  //   imageUrl: "images/Compatibilidades.png", // teal-600
  //   baseColor: "teal",
    
  //   link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/Listado%20de%20usos%20y%20actividades%20sujetos%20a%20controles%20ambientales%20Regi%C3%B3n%20Chapala.pdf",
  //   target: "_blank",
  // },
];
const items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "images/vectoriales.png", // sky-600
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/vectorialeschapala.zip",
    target: "_blank",
  },
  {
    title: "Fichas UGAs",
    description: "Información detallada de cada Unidad de Gestión Ambiental (UGA).",
    imageUrl: "https://placehold.co/64x64/059669/ffffff?text=LIST", // green-600
    baseColor: "green",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/Fichas%20de%20las%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Chapala.pdf",
    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio",
    img: <img src="images/Criterios.png" alt="Criterios" className="w-16 h-16 mb-4" />,
    icon: <FaFilePdf className="text-5xl text-white" />,
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Descargas/Criterios%20Regi%C3%B3n%20Chapala.pdf",
  },
  {
    title: "Estrategias Ecologicas",
    description: "Proyectos de orientación para la protección del entorno natural.",
    img: <img src="images/Estrategias.png" alt="Estrategias" className="w-16 h-16 mb-4" />,
    icon: <FaProjectDiagram className="text-5xl text-white" />,
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Descargas/Estrategias%20Regi%C3%B3n%20Chapala.pdf",
  },
  {
    title: "Lineamientos",
    description: "Conservar,proteger y restaurar el equilibrio ecológico del territorio.",
    img: <img src="images/Lineamientos.png" alt="Lineamientos" className="w-16 h-16 mb-4" />,
    icon: <FaLightbulb className="text-5xl text-white" />,
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Chapala.pdf",
  },
];

const ChapalaMain = [
  {
    id: 0,
    title: "Publicación en Periódico Oficial",
    subtitle: "Decreto y publicación del POETR",
    icon: <Book className="w-5 h-5 text-green-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Elaboracion/12_11_21_iv_0.pdf"
  },
  {
    id: 1,
    title: "POETR Región Chapala",
    subtitle: "Programa de Ordenamiento Ecológico y Territorial Regional",
    icon: <Map className="w-5 h-5 text-emerald-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Territorial%20Regi%C3%B3n%20Chapala.pdf"
  },
  {
    id: 2,
    title: "Plan Regional de Integración Urbana",
    subtitle: "Desarrollo urbano integral de la región",
    icon: <Target className="w-5 h-5 text-rose-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/PRIU%20Regi%C3%B3n%20Chapala.pdf"
  }
];

const municipalities = [
  {
    id: 1,
    name: 'Chapala',
    shield: 'images/chapala/Chapala.png',
    description: 'Destacado por la pesca artesanal, la agricultura de riego y el turismo lacustre, cuenta con servicios turísticos y actividades recreativas en la ribera del lago de Chapala. Sus humedales y áreas ribereñas permiten la observación de aves acuáticas y fomentan programas de conservación.',
    population: '55,196 habitantes',
    area: '630 km²',
    foundation: '1896',
    attractions: 'El malecón y la ribera del Lago de Chapala, junto con espacios como el Centro Cultural Antigua Presidencia, ofrecen escenarios ideales para recorridos en embarcación, observación de aves acuáticas y actividades culturales, la artesanía y la gastronomía local. ',
    color: 'from-blue-500 to-teal-600',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala, Área Natural Protegida Área Estatal de Protección Hidrológica Cerro Viejo-Chupinaya-Los Sabinos y Área Estatal de Protección Hidrológica Cerro San Miguel-Chiquihuitillo.',
    NumerodeUgas: '8 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Ciénega',
  },
  {
    id: 2,
    name: 'Jamay',
    shield: 'images/chapala/Jamay.png',
    description: 'Con agricultura de riego centrada en hortalizas y maíz, ganadería menor y pequeñas industrias locales de alimentos y textiles, se ubica en llanuras cercanas al lago de Chapala. Su territorio facilita el ecoturismo rural.',
    population: '24,894 habitantes',
    area: '162.8 km km²',
    foundation: '1914',
    attractions: 'La Laguna de Jamay, el Balneario Comala y miradores naturales en la ribera conforman paisajes ideales para el senderismo y la observación de aves. Destacan también sus zonas agrícolas tradicionales, que permiten recorridos rurales y experiencias gastronómicas vinculadas a la producción local. ',
    color: 'from-green-500 to-emerald-600',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala, Área Natural Protegida Área Estatal de Protección Hidrológica Cerro Viejo-Chupinaya-Los Sabinos y Área Estatal de Protección Hidrológica Cerro San Miguel-Chiquihuitillo.',
    NumerodeUgas: '8 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Ciénega',
  },
  {
    id: 3,
    name: 'Jocotepec',  
    shield: 'images/chapala/Jocotepec.png',
    description: 'Destacado por la agricultura de riego, pesca en el lago de Chapala, ganadería menor y actividades industriales ligeras, se desarrolla en valles y planicies con vegetación riparia. Su entorno favorece el ecoturismo lacustre y rural.',
    population: '47,105 habitantes',
    area: '324.3 km km²',
    foundation: '1896',
    attractions: 'La ribera del Lago de Chapala, el Parque La Cristalera y su Centro Histórico ofrecen escenarios para recorridos en embarcación, senderismo y observación de aves acuáticas. El municipio destaca además por talleres artesanales y gastronomía regional asociada a los paisajes lacustres.',
    color: 'from-amber-500 to-orange-600',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala, Área Natural Protegida Área Estatal de Protección Hidrológica Cerro Viejo-Chupinaya-Los Sabinos.',
    NumerodeUgas: '11 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Ciénega',
  },
  {
    id: 4,
    name: 'Ocotlán',
    shield: 'images/chapala/Ocotlan.png',
    description: 'Con agricultura de riego de maíz, caña y hortalizas, ganadería menor y un sector manufactura mueblera activo, se localiza en llanuras atravesadas por arroyos intermitentes. Estas condiciones permiten el desarrollo de turismo rural.',
    population: '106,050 habitantes',
    area: '242.7  km²',
    foundation: '1906',
    attractions: 'La ribera de la Laguna de Ocotlán, el Centro Histórico y la Plaza Principal conforman espacios propicios para recorridos culturales y observación de aves. Sus tradiciones artesanales y la gastronomía local.',
    color: 'from-purple-500 to-indigo-600',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala y Área Natural Protegida Área Estatal de Protección Hidrológica Sierra Cóndiro-Canales.',
    NumerodeUgas: '14 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Ciénega',
  },
  {
    id: 5,
    name: 'Poncitlán',
    shield: 'images/chapala/Poncitlan.png',
    description: 'Sustentado en agricultura de riego, pesca y ganadería menor, con actividad industrial y comercial en pequeñas empresas, se desarrolla en llanuras cercanas al lago de Chapala con humedales y vegetación ribereña. Su entorno natural permite el ecoturismo lacustre y rural.',
    population: '53,659 habitantes',
    area: '834.9 km²',
    foundation: '1888',
    attractions: 'Miradores naturales, la ribera del Lago de Chapala y diversos cuerpos de agua permiten recorridos en embarcación, senderismo y observación del paisaje. Destacan también sus zonas agrícolas y actividades comunitarias vinculadas a la producción tradicional.',
    color: 'from-green-500 to-indigo-600',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala y Área Natural Protegida Área Estatal de Protección Hidrológica Cerro San Miguel-Chiquihuitillo.',
    NumerodeUgas: '12 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Ciénega',
  },
  {
    id: 6,
    name: 'Tizapán el Alto',
    shield: 'images/chapala/tizapan_el_alto.png',
    description: 'Con agricultura de riego, pesca, ganadería menor y actividad turística en la ribera del lago de Chapala, se ubica en laderas y valles con arroyos y humedales. Su territorio favorece el ecoturismo lacustre y rural.',
    population: '22,758 habitantes',
    area: '193.9 km²',
    foundation: '1896',
    attractions: 'La ribera del Lago de Chapala, el Parque Ecológico El Batán y sus zonas recreativas, el Arroyo Tizapán, paisajes ideales para el senderismo, recorridos lacustres y observación de fauna. Sobresale por talleres de artesanía y gastronomía local ',
    color: 'from-blue-800 to-blue-900',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala',
    NumerodeUgas: '7 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Sureste',
  },
  {
    id: 7,
    name: 'Tuxcueca',
    shield: 'images/chapala/Tuxcueca.png',
    description: 'Destacado por la pesca artesanal, agricultura de riego, ganadería menor y turismo lacustre, se localiza en las orillas del lago de Chapala con humedales y vegetación ribereña. Su entorno permite el turismo recreativo y rural',
    population: '6,702 habitantes',
    area: '132.4 km km²',
    foundation: '1886',
    attractions: ' Áreas recreativas del municipio conforman espacios para recorridos lacustres, senderismo como la vista de los cerros de García y El Picacho, visitas a comunidades rurales. Destacan actividades agrícolas tradicionales y gastronomía regional.',
    color: 'from-purple-800 to-indigo-600',
    EcologicaZona: 'Sitio RAMSAR Lago de Chapala.',
    NumerodeUgas: '8 Unidades de Gestión Ambiental (UGA).',
    administrativeRegion: 'Sureste',
  },
];


const ChapalaPage = () => {
  const [selectedMunicipality, setSelectedMunicipality] = useState(municipalities[0]);
  const aboutLinks = [
    
    { text: 'Programa de Ordenamiento Ecológico y Territorial Regional', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Territorial%20Regi%C3%B3n%20Chapala.pdf' },
    { text: 'Plan Regional de Integración Urbana', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Descargas/PRIU%20Regi%C3%B3n%20Chapala.pdf' },
    { text: 'Programas Municipales de Desarrollo Urbano (Ver en Inicio)', url: '/descargas/chapala' },
    
  ];

  const aboutDates = [
    { label: 'Inicio de proceso de formulación', date: '14/06/2019' },
    { label: 'Inicio de Consulta Pública', date: '22/07/2020' },
    { label: 'Fin de Consulta Pública', date: '23/11/2020' },
    { label: 'Sesión de validación', date: '01/06/2021' },
    { label: 'Fecha Publicación', date: '11/12/2021' },
  ];
  
  const bitacoraItems = [
    {
      title: 'FASE DE FORMULACIÓN',
      icon: FileSignature,
      color: 'from-blue-500 to-cyan-500',
      content: [
        // {
        //   subtitle: 'Acuerdos de Integración Municipal - Región Chapala',
        //   items: [
        //     { text: 'Acuerdo de integración - Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Chapala.pdf' },
        //     { text: 'Acuerdo de integración - Jamay', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Jamay.pdf' },
        //     { text: 'Acuerdo de integración - Jocotepec', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Jocotepec.pdf' },
        //     { text: 'Acuerdo de integración - Ocotlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Ocotlan.pdf' },
        //     { text: 'Acuerdo de integración - Poncitlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Poncitlan.pdf' },
        //     { text: 'Acuerdo de integración - Tizapán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Tizapan.pdf' },
        //     { text: 'Acuerdo de integración - Tuxcueca', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Gestion/Inicio/Acuerdo_integracion_Tuxcueca.pdf' }
        //   ]
        // },
        {
          subtitle: 'Actas de Aprobación Municipal',
          items: [
            { text: 'Acta de Aprobación Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/01_Acta_chapala.pdf' },
            { text: 'Acta de Aprobación Jamay', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/02_Acta_Jamay.pdf' },
            { text: 'Acta de Aprobación Jocotepec', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/03_Acta_Jocotepec.pdf' },
            { text: 'Acta de Aprobación Ocotlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/04_Acta_Ocotlan.pdf' },
            { text: 'Acta de Aprobación Poncitlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/05_Acta_Poncitlan.pdf' },
            { text: 'Acta de Aprobación Tizapán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/06_Acta_Tizapan.pdf' },
            { text: 'Acta de Aprobación Tuxcueca', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/actasaprobacion/07_Acta_Tuxcueca.pdf' }
          ]
        },
        {
          subtitle: 'Dictámenes de Congruencia PMDU',
          items: [
            { text: 'Dictamen de Congruencia PMDU Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/08_DC_PMDU_Chapala.pdf' },
            { text: 'Dictamen de Congruencia PMDU Jamay', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/09_DC_PMDU_Jamay.pdf' },
            { text: 'Dictamen de Congruencia PMDU Jocotepec', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/10_DC_PMDU_Jocotepec.pdf' },
            { text: 'Dictamen de Congruencia PMDU Ocotlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/11_DC_PMDU_Ocotlan.pdf' },
            { text: 'Dictamen de Congruencia PMDU Poncitlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/12_DC_PMDU_Poncitlan.pdf' },
            { text: 'Dictamen de Congruencia PMDU Tizapán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/13_DC_PMDU_Tizapan.pdf' },
            { text: 'Dictamen de Congruencia PMDU Tuxcueca', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Dictamen/14_DC_PMDU_Tuxcueca.pdf' }
          ]
        },
        {
          subtitle: 'Convenios',
          items: [
            { text: 'Convenio de Coordinación Estado-Municipios Región Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/convenio/01_Convenio_Estado_Muinicipios_Chapala.pdf' },
            { text: 'Convenio Marco de Coordinación Estado – Federación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/convenio/02_Convenio_Marco_Estado_Federacion.pdf' },
            { text: 'Convenio Específico de Coordinación Estado-Federación Región Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/convenio/03_Convenio_Region_Chapala_VF_FIRMADO.pdf' }
          ]
        },
        {
          subtitle: 'Actas del Consejo Regional',
          items: [
            { text: 'Acta de Instalación y Primera Ordinaria CA 2020', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Consejo/01_ACTA_DE_INSTALACION_Y_PRIMERA_ORDINARIA_2020.pdf' },
            { text: 'Acta de Primera Ordinaria CA 2021', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Consejo/03_ACTA_DE_LA_PRIMERA_SESION_ORDINARIA_2021.pdf' }
          ]
        }
      ]
    },
    {
      title: 'FASE DE EXPEDICIÓN',
      icon: FileSpreadsheet,
      color: 'from-emerald-500 to-green-500',
      content: [
        {
          subtitle: 'Consulta y Participación Ciudadana',
          items: [
            { text: 'Documento puesto en Consulta Pública', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Elaboracion/01_PROETDUMChapala_1_0_CONSULTA.pdf' },
            { text: 'Respuestas Consulta Pública Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Elaboracion/02_RepuestasConsultaPublicaChapala.pdf' }
          ]
        },
        {
          subtitle: 'Documentos de Publicación',
          items: [
            { text: 'Publicación Periódico del Estado Región Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Elaboracion/12_11_21_iv_0.pdf' }
          ]
        }
      ]
    },
    {
      title: 'FASE DE EJECUCIÓN',
      icon: Zap,
      color: 'from-amber-500 to-orange-500',
      content: [
        {
          subtitle: 'Seguimiento y Monitoreo',
          items: [
            { text: 'Boleta Registral Región Chapala', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Chapala/Elaboracion/BoletaRegistralChapala.jpeg' }
          ]
        }
      ]
    },
    {
      title: 'FASE DE EVALUACIÓN',
      icon: BarChart3,
      color: 'from-purple-500 to-violet-500',
      content: [
        {
          subtitle: 'Evaluaciones Programadas',
          items: [
            { text: 'Estudios Técnicos', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Evaluacion/EstudiosTecnicos.zip' },
            { text: 'Informes de Evaluación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/chapala/Evaluacion/Informes_de_Evaluacion.zip' }
          ]
        }
      ]
    }
  ];

  return (
    <div className="environmental-theme fade-in">
      <ModernBanner onScrollClick={scrollToSection} />

      <section id="introduccion" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 
              rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700"
          >
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="lg:w-2/3">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <Info className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Ordenamiento Ecológico Regional
                    <span className="block text-lg text-gray-600 dark:text-gray-300 mt-1">
                      Región Chapala
                    </span>
                  </h2>
                </div>
                
                <div className="space-y-4 text-gray-700 dark:text-gray-300 text-justify">
                  <p>
                    El <strong>Gobierno del Estado de Jalisco</strong> impulsa el desarrollo sustentable 
                    en la región <strong>Chapala</strong>, integrada por los municipios de 
                    <strong> Chapala, Jamay, Jocotepec, Poncitlán, Ocotlán, Tizapán el Alto y Tuxcueca</strong>.
                  </p>
                  
                  <p>
                    Para impulsar dicho desarrollo, en el año <strong>2019</strong> se firmó el <strong>Convenio de Coordinación</strong> para el 
                    ordenamiento territorial, el cual garantiza la realización de las actividades programadas en estrecha colaboración entre los diferentes órdenes de gobierno: 
                    <strong> Estado y Municipios</strong>.
                  </p>
                  
                  <p>
                    Asimismo, se firmó con la Federación un <strong>Convenio Marco de Coordinación</strong> junto con <strong>SEMARNAT Y SEDATU</strong> 
                    en el año <strong>2020</strong>. Estos convenios establecen los fundamentos para la colaboración en el proceso de elaboración de los instrumentos de ordenamiento territorial.
                  </p>
                  
                  <p>
                    Derivado de esto se inició con el proceso de elaboración de los instrumentos, seguido por el <strong>proceso de consulta pública</strong>, 
                    la cual incluyó amplia participación de los sectores que influyen en el territorio. Las fechas más relevantes sobre 
                    el proceso de elaboración se describen a continuación:
                  </p>
                </div>
              </div>
              
              <div className="lg:w-1/3 w-full">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-2" />
                    Cronología Clave
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3 animate-pulse" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Inicio de formulación:</strong><br /> 14 de junio de 2019
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3 animate-pulse" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Inicio de Consulta Pública:</strong><br /> 22 de julio de 2020
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3 animate-pulse" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Fin de Consulta Pública:</strong><br /> 23 de noviembre de 2020
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Sesión de validación:</strong><br /> 01 de junio de 2021
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Fecha Publicación oficial:</strong><br /> 11 de diciembre de 2021
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="DocumentosE" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <DocumentRepository2
          items={ChapalaMain}
          onDownload={(doc) => window.open(doc.url, '_blank')}
          showFooter={false}
        />
      </section>

      <section id="Descargas" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <Descargas dataItems={Items} />
      </section>
    
      
      {/* <section className="municipalities-section py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-green-800">
            <MapPinIcon className="inline-block mr-3 mb-1" size={36} />
            Municipios de la Región Chapala
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {municipalities.map((municipality) => (
              <div 
                key={municipality.id}
                className={`municipality-card rounded-lg overflow-hidden shadow-lg transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105 ${selectedMunicipality.id === municipality.id ? 'ring-4 ring-green-500' : ''}`}
                onClick={() => setSelectedMunicipality(municipality)}
              >
                <div className={`h-24 bg-gradient-to-r ${municipality.color} flex items-center justify-center`}>
                  <img 
                    src={municipality.shield} 
                    alt={`Escudo de ${municipality.name}`} 
                    className="h-16 w-16 object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h3 className="text-xl font-bold text-center text-gray-800">{municipality.name}</h3>
                </div>
              </div>
            ))}
          </div>

          
          <div className="municipality-details bg-white rounded-xl shadow-md p-6 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-full md:w-1/3 flex justify-center">
                <img 
                  src={selectedMunicipality.shield} 
                  alt={`Escudo de ${selectedMunicipality.name}`} 
                  className="h-40 w-40 object-contain"
                />
              </div>
              <div className="w-full md:w-2/3">
                <h3 className="text-2xl font-bold mb-4 text-gray-800">{selectedMunicipality.name}</h3>
                <p className="text-gray-600 mb-4">{selectedMunicipality.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-center">
                    <Users className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Población:</strong> {selectedMunicipality.population}</span>
                  </div>
                  <div className="flex items-center">
                    <Map className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Superficie:</strong> {selectedMunicipality.area}</span>
                  </div>
                  <div className="flex items-center">
                    <Landmark className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Fundación:</strong> {selectedMunicipality.foundation}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Atracciones:</strong> {selectedMunicipality.attractions}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}
      {/* <section className="municipalities-section py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-green-800">
            <MapPinIcon className="inline-block mr-3 mb-1" size={36} />
            Municipios de la Región Chapala
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {municipalities.map((municipality) => (
              <div 
                key={municipality.id}
                className={`municipality-card rounded-lg overflow-hidden shadow-lg transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105 ${selectedMunicipality.id === municipality.id ? 'ring-4 ring-green-500' : ''}`}
                onClick={() => setSelectedMunicipality(municipality)}
              >
                <div className={`h-24 bg-gradient-to-r ${municipality.color} flex items-center justify-center`}>
                  <img 
                    src={municipality.shield} 
                    alt={`Escudo de ${municipality.name}`} 
                    className="h-16 w-16 object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h3 className="text-xl font-bold text-center text-gray-800">{municipality.name}</h3>
                </div>
              </div>
            ))}
          </div>

          
          <div className="municipality-details bg-white rounded-xl shadow-md p-6 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-full md:w-1/3 flex justify-center">
                <img 
                  src={selectedMunicipality.shield} 
                  alt={`Escudo de ${selectedMunicipality.name}`} 
                  className="h-40 w-40 object-contain"
                />
              </div>
              <div className="w-full md:w-2/3">
                <h3 className="text-2xl font-bold mb-4 text-gray-800">{selectedMunicipality.name}</h3>
                <p className="text-gray-600 mb-4">{selectedMunicipality.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-center">
                    <Users className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Población:</strong> {selectedMunicipality.population}</span>
                  </div>
                  <div className="flex items-center">
                    <Map className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Superficie:</strong> {selectedMunicipality.area}</span>
                  </div>
                  <div className="flex items-center">
                    <Landmark className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Fundación:</strong> {selectedMunicipality.foundation}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="text-green-600 mr-3" size={20} />
                    <span className="text-gray-700"><strong>Atracciones:</strong> {selectedMunicipality.attractions}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}
      {/* <Gallery imagesProp={imageList} itemsPerPage={4} /> */}
       <section className="municipalities-section py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-gray-900 dark:to-gray-800 min-h-screen font-sans">
      <div className="container mx-auto max-w-7xl">
        
        {/* --- Título Principal --- */}
        <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 dark:text-teal-400 text-center mb-16 relative">
          <MapPin className="inline-block mr-3 mb-1 w-10 h-10" />
          Municipios de la Región : Chapala
          <p className="text-lg font-normal text-gray-600 dark:text-gray-400 mt-2">
            Conoce los municipios y sus características clave.
          </p>
        </h2>
        
        {/* --- Grid de Tarjetas (Selector) --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {municipalities.map((municipality) => (
            <div 
              key={municipality.id}
              onClick={() => setSelectedMunicipality(municipality)}
              className={`
                municipality-card 
                rounded-2xl 
                overflow-hidden 
                shadow-xl 
                transition-all 
                duration-500 
                cursor-pointer 
                hover:shadow-2xl 
                hover:scale-[1.03]
                bg-white/90 dark:bg-gray-800/80
                backdrop-blur-sm
                border border-gray-100 dark:border-gray-700
                ${selectedMunicipality.id === municipality.id 
                  ? 'ring-4 ring-teal-500 scale-[1.03] shadow-teal-300/50 dark:shadow-teal-700/50' 
                  : 'hover:ring-2 hover:ring-teal-300'
                }
              `}
            >
              <div className={`h-28 ${municipality.color} flex flex-col items-center justify-center p-3 text-white font-bold`}>
                <img 
                  src={municipality.shield} 
                  alt={`Escudo de ${municipality.name}`} 
                  className="h-14 w-14 object-contain mb-1 drop-shadow-md"
                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/000000/ffffff?text=E"; }}
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-extrabold text-center text-gray-900 dark:text-white">{municipality.name}</h3>
                <p className="text-xs text-center text-teal-600 dark:text-teal-400 mt-1 flex items-center justify-center">
                    Ver Detalles <ChevronRight className='w-3 h-3 ml-1'/>
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* --- Detalles del municipio seleccionado (Modern Panel) --- */}
        <div 
            key={selectedMunicipality.id} // Key para forzar la animación en cada selección
            className="municipality-details bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 max-w-6xl mx-auto border border-teal-200 dark:border-teal-900 animate-fade-in-up"
            style={{ '--delay': '200ms' }}
        >
          <div className="flex flex-col md:flex-row items-start gap-10">
            
            {/* Escudo y Descripción */}
            <div className="w-full md:w-1/3 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <img 
                src={selectedMunicipality.shield} 
                alt={`Escudo de ${selectedMunicipality.name}`} 
                className="h-48 w-48 object-contain shadow-lg rounded-full p-2 bg-white dark:bg-gray-900 mb-6 border-4 border-teal-300 dark:border-teal-500"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x192/000000/ffffff?text=E"; }}
              />
              <h3 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white text-center">{selectedMunicipality.name}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm text-center italic mt-2">{selectedMunicipality.description}</p>
            </div>
            
            {/* Información Detallada (Badges) */}
            <div className="w-full md:w-2/3">
                
                <h4 className="text-2xl font-bold text-teal-700 dark:text-teal-400 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center">
                    <Info className='w-5 h-5 mr-2'/> Datos Clave
                </h4>

                {/* Grid de Métricas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DetailBadge icon={Users} label="Población" value={selectedMunicipality.population} />
                    <DetailBadge icon={Map} label="Superficie" value={selectedMunicipality.area} />
                    <DetailBadge icon={Calendar} label="Municipalización" value={selectedMunicipality.foundation} />
                    <DetailBadge icon={Landmark} label="Región Administrativa" value={selectedMunicipality.administrativeRegion} />
                    <DetailBadge icon={MapPin} label="Número de UGAs" value={selectedMunicipality.NumerodeUgas} />
                    <DetailBadge icon={MountainSnow} label="Zonas de Importancia Ecológica" value={selectedMunicipality.EcologicaZona} />
                </div>
                
                {/* Atracciones (Full Width) */}
                <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Waves className='text-blue-500 mr-2 w-5 h-5'/> Principales Atracciones
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                        {selectedMunicipality.attractions}
                    </p>
                </div>
              
            </div>
          </div>
        </div>
      </div>
    </section>

      <FullWidthVisorWithSidebar />

      <ModernBitacoraSection items={bitacoraItems} />
      <FasesSection 
        imageUrl="images/FasesOrdenamiento.png"
        altText="Fases del Ordenamiento Chapala"
      />
     
    </div>
  );
};

export default ChapalaPage;